import { useState } from 'react'
import { ChevronDown, ChevronUp, Sparkles, Bug, Wrench } from 'lucide-react'
import { Button } from '@/components/ui/button'
import updatesJson from '@/config/updates.json'

interface Update {
  version: string
  date: string
  title: string
  description: string
  type: 'feature' | 'improvement' | 'bugfix'
}

interface UpdatesData {
  currentVersion: string
  updates: Update[]
}

const getUpdateIcon = (type: string) => {
  switch (type) {
    case 'feature':
      return <Sparkles className="w-3 h-3 text-blue-500" />
    case 'improvement':
      return <Wrench className="w-3 h-3 text-green-500" />
    case 'bugfix':
      return <Bug className="w-3 h-3 text-orange-500" />
    default:
      return <Sparkles className="w-3 h-3 text-gray-500" />
  }
}

const getUpdateTypeColor = (type: string) => {
  switch (type) {
    case 'feature':
      return 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20'
    case 'improvement':
      return 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20'
    case 'bugfix':
      return 'text-orange-600 bg-orange-50 dark:text-orange-400 dark:bg-orange-900/20'
    default:
      return 'text-gray-600 bg-gray-50 dark:text-gray-400 dark:bg-gray-900/20'
  }
}

export function RecentUpdates() {
  const [isExpanded, setIsExpanded] = useState(false)
  const updatesData = (updatesJson as unknown) as UpdatesData
  const latestUpdate = updatesData?.updates?.[0]

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium mb-3">Recent Updates</h3>
      
      {/* Current Version */}
      <div className="text-xs text-muted-foreground mb-3">
        Current Version: <span className="font-medium text-foreground">v{updatesData?.currentVersion || 'N/A'}</span>
      </div>

      {/* Latest Update */}
      <div className="space-y-2">
        <div
          className="p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {latestUpdate ? getUpdateIcon(latestUpdate.type) : <Sparkles className="w-3 h-3 text-gray-500" />}
              <div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium">{latestUpdate?.title || 'No updates yet'}</span>
                  {latestUpdate && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getUpdateTypeColor(latestUpdate.type)}`}>
                      {latestUpdate.type}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {latestUpdate ? (
                    <>v{latestUpdate.version} • {latestUpdate.date}</>
                  ) : '—'}
                </div>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </Button>
          </div>
          
          {isExpanded && latestUpdate && (
            <div className="mt-2 text-xs text-muted-foreground">
              {latestUpdate.description}
            </div>
          )}
        </div>

        {/* All Updates (when expanded) */}
        {isExpanded && updatesData?.updates?.length > 1 && (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {updatesData.updates.slice(1).map((update, index) => (
              <div key={index} className="p-2 rounded border bg-muted/30">
                <div className="flex items-center space-x-2">
                  {getUpdateIcon(update.type)}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-medium">{update.title}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${getUpdateTypeColor(update.type)}`}>
                        {update.type}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      v{update.version} • {update.date}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {update.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
